import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { UserPlus, Pencil, Power, Users, UsersRound, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

const ROLE_BADGE = {
  super_admin: "bg-violet-100 text-violet-800",
  "super-admin": "bg-violet-100 text-violet-800",
  dean: "bg-blue-100 text-blue-800",
  counselor: "bg-emerald-100 text-emerald-800",
};
const ROLE_LABEL = { super_admin: "Super Admin", "super-admin": "Super Admin", dean: "Dean", counselor: "Counselor" };

function initials(name = "") {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "?";
}

const EMPTY_CREATE = { name: "", email: "", password: "", role: "counselor", campus: "", max_capacity: 50 };

export default function StaffManagement() {
  const { user, impersonation } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [seats, setSeats] = useState(null); // { used, limit, tier, price_per_additional_seat, at_capacity }
  const [manageQuotaOpen, setManageQuotaOpen] = useState(false);
  // Platform admins get this shortcut whether logged in directly OR currently
  // impersonating a tenant (impersonation can only ever have been initiated by
  // a real platform admin in the first place — see require_platform_admin).
  const canManageQuota = Boolean(user?.is_platform_admin || impersonation);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/staff")
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);
  const loadSeats = useCallback(() => {
    api.get("/admin/seats").then((r) => setSeats(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); loadSeats(); }, [load, loadSeats]);

  const toggleStatus = async (s) => {
    try {
      await api.patch(`/admin/staff/${s.id}/status`, { is_active: !s.is_active });
      toast.success(`${s.name} ${s.is_active ? "deactivated" : "reactivated"}`);
      load();
      loadSeats();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="staff-management-page" className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6" /> Staff &amp; Counselors
          </h1>
          <p className="text-sm text-slate-500 mt-1">Manage your counseling team — add members, reset logins, control access.</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <SeatCounter seats={seats} />
          {canManageQuota && (
            <Button className="h-7 text-xs border-slate-300 hover:bg-slate-100 text-slate-700" size="sm" variant="outline"
              data-testid="manage-quota-button" onClick={() => setManageQuotaOpen(true)}>
              <SlidersHorizontal className="w-3.5 h-3.5 mr-1" /> Manage Quota
            </Button>
          )}
          <Button className="bg-brand w-full sm:w-auto" data-testid="add-staff-button" onClick={() => setAddOpen(true)}>
            <UserPlus className="w-4 h-4 mr-1.5" /> Add staff member
          </Button>
        </div>
      </div>

      {seats?.at_capacity && (
        <Card className="border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="seat-limit-banner">
          You've used all {seats.limit} counselor seats on the {seats.tier} plan. Deactivate an unused
          counselor to free a seat, or ask your platform administrator to raise the limit
          (additional seats are ₹{Number(seats.price_per_additional_seat).toLocaleString("en-IN")}/mo).
        </Card>
      )}

      <Card className="border-slate-200" data-testid="staff-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {["Name", "Email", "Role", "Status", "Assigned Leads", "Capacity", ""].map((h) => (
                  <TableHead key={h} className="text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">Loading…</TableCell></TableRow>}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400" data-testid="staff-empty">No staff yet.</TableCell></TableRow>
              )}
              {!loading && rows.map((s, i) => (
                <TableRow key={s.id} data-testid={`staff-row-${i}`} className={s.is_active ? "" : "opacity-60"}>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <span className="h-8 w-8 rounded-full bg-slate-100 text-slate-600 text-xs font-bold grid place-items-center shrink-0">{initials(s.name)}</span>
                      <span className="font-medium text-slate-800">{s.name}</span>
                      {s.id === user?.id && <Badge variant="secondary" className="text-[10px]">you</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600">{s.email}</TableCell>
                  <TableCell>
                    <Badge className={ROLE_BADGE[s.role] || "bg-slate-100 text-slate-700"}>{ROLE_LABEL[s.role] || s.role}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={s.is_active ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"} data-testid={`staff-status-${i}`}>
                      {s.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono">{s.current_load}</TableCell>
                  <TableCell className="font-mono text-slate-500">{s.role === "counselor" ? (s.max_capacity || "∞") : "—"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="icon" title="Edit" data-testid={`staff-edit-${i}`} onClick={() => setEditing(s)}>
                        <Pencil className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" title={s.is_active ? "Deactivate" : "Reactivate"}
                        data-testid={`staff-toggle-${i}`} disabled={s.id === user?.id}
                        onClick={() => toggleStatus(s)}>
                        <Power className={`w-4 h-4 ${s.is_active ? "text-red-500" : "text-emerald-600"}`} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {addOpen && (
        <AddStaffModal seats={seats} onClose={() => setAddOpen(false)}
          onDone={() => { setAddOpen(false); load(); loadSeats(); }} />
      )}
      {editing && (
        <EditStaffModal staff={editing} seats={seats} onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); load(); loadSeats(); }} />
      )}
      {manageQuotaOpen && (
        <ManageQuotaModal institutionId={user?.institution_id} seats={seats}
          onClose={() => setManageQuotaOpen(false)}
          onDone={() => { setManageQuotaOpen(false); loadSeats(); }} />
      )}
    </div>
  );
}

function SeatCounter({ seats }) {
  if (!seats) return null;
  const pct = seats.limit > 0 ? Math.min(100, Math.round((seats.used / seats.limit) * 100)) : 0;
  const tone = seats.at_capacity ? "text-red-700" : pct >= 80 ? "text-amber-700" : "text-slate-700";
  const barTone = seats.at_capacity ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-brand";
  return (
    <div className="hidden sm:flex flex-col items-end gap-1 pr-1" data-testid="seat-counter">
      <div className={`flex items-center gap-1.5 text-xs font-semibold ${tone}`}>
        <UsersRound className="w-3.5 h-3.5" />
        <span data-testid="seat-counter-text">{seats.used} / {seats.limit} Seats Used</span>
        <Badge variant="secondary" className="text-[10px] capitalize">{seats.tier}</Badge>
      </div>
      <div className="w-32 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${barTone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const PLAN_TIERS = ["starter", "growth", "scale", "enterprise"];

// Platform-admin-only shortcut to adjust this institution's seat plan right
// from /staff, without a round trip to /platform/tenants. Same
// PATCH /platform/tenants/:id/plan endpoint as PlatformTenants.jsx's own
// PlanDialog, just pre-scoped to the current institution (works while
// impersonating too — see routes_auth.require_platform_admin_or_impersonating).
function ManageQuotaModal({ institutionId, seats, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    tier: seats?.tier || "starter",
    counselor_seat_limit: seats?.limit ?? 5,
    price_per_additional_seat: seats?.price_per_additional_seat ?? 1500,
  });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const valid = PLAN_TIERS.includes(f.tier) && Number(f.counselor_seat_limit) >= 0 && Number(f.price_per_additional_seat) >= 0;

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/platform/tenants/${institutionId}/plan`, {
        tier: f.tier,
        counselor_seat_limit: Number(f.counselor_seat_limit),
        price_per_additional_seat: Number(f.price_per_additional_seat),
      });
      toast.success("Seat quota updated");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="manage-quota-modal" className="max-w-md">
        <DialogHeader><DialogTitle>Manage seat quota</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {seats && (
            <p className="text-xs text-slate-500">
              Currently using {seats.used} of {seats.limit} counselor seats.
            </p>
          )}
          <Field label="Tier">
            <Select value={f.tier} onValueChange={set("tier")}>
              <SelectTrigger data-testid="quota-tier"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLAN_TIERS.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Counselor seat limit">
            <Input data-testid="quota-seat-limit" type="number" min={0} value={f.counselor_seat_limit}
              onChange={(e) => set("counselor_seat_limit")(e.target.value)} />
          </Field>
          <Field label="Price per additional seat (₹/mo)">
            <Input data-testid="quota-seat-price" type="number" min={0} value={f.price_per_additional_seat}
              onChange={(e) => set("price_per_additional_seat")(e.target.value)} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="bg-brand" data-testid="quota-submit" disabled={busy || !valid} onClick={submit}>
            {busy ? "Saving…" : "Save quota"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, hint }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function AddStaffModal({ onClose, onDone, seats }) {
  const [f, setF] = useState(EMPTY_CREATE);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const seatBlocked = f.role === "counselor" && seats?.at_capacity;
  const valid = f.name.trim().length >= 2 && /\S+@\S+\.\S+/.test(f.email) && f.password.length >= 12 && !seatBlocked;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/staff", {
        name: f.name.trim(),
        email: f.email.trim().toLowerCase(),
        password: f.password,
        role: f.role,
        campus: f.campus.trim() || null,
        max_capacity: Number(f.max_capacity) || 0,
      });
      toast.success(`${data.name} added as ${data.role}`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-staff-modal" className="max-w-md">
        <DialogHeader><DialogTitle>Add staff member</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Full name">
            <Input data-testid="staff-name" value={f.name} onChange={(e) => set("name")(e.target.value)} placeholder="Priya Sharma" />
          </Field>
          <Field label="Login email">
            <Input data-testid="staff-email" type="email" value={f.email} onChange={(e) => set("email")(e.target.value)} placeholder="priya@aurora.edu" />
          </Field>
          <Field label="Temporary password" hint="At least 12 characters; the member can change it after first login.">
            <Input data-testid="staff-password" type="text" value={f.password} onChange={(e) => set("password")(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Role">
              <Select value={f.role} onValueChange={set("role")}>
                <SelectTrigger data-testid="staff-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="counselor">Counselor</SelectItem>
                  <SelectItem value="dean">Dean</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {f.role === "counselor" && (
              <Field label="Lead capacity">
                <Input data-testid="staff-capacity" type="number" min={0} value={f.max_capacity}
                  onChange={(e) => set("max_capacity")(e.target.value)} />
              </Field>
            )}
          </div>
          {f.role === "counselor" && (
            <Field label="Campus (optional)">
              <Input data-testid="staff-campus" value={f.campus} onChange={(e) => set("campus")(e.target.value)} placeholder="Main Campus" />
            </Field>
          )}
          {seatBlocked && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="staff-seat-blocked">
              Counselor seat limit reached ({seats.used}/{seats.limit} on the {seats.tier} plan). Deactivate an
              unused counselor or ask your platform administrator to raise the limit.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="bg-brand" data-testid="staff-create-submit" disabled={!valid || busy} onClick={submit}>
            {busy ? "Adding…" : "Add member"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditStaffModal({ staff, onClose, onDone, seats }) {
  const [f, setF] = useState({
    name: staff.name || "",
    email: staff.email || "",
    role: staff.role === "dean" ? "dean" : "counselor",
    campus: staff.campus || "",
    max_capacity: staff.max_capacity ?? 50,
    new_password: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  // Super Admin role can't be reassigned from this screen; self-role changes are blocked server-side.
  const roleLocked = staff.role === "super_admin" || staff.role === "super-admin";
  // Promoting a non-counselor (dean) into "counselor" consumes a seat.
  const seatBlocked = f.role === "counselor" && staff.role !== "counselor" && seats?.at_capacity;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { name: f.name.trim(), email: f.email.trim().toLowerCase() };
      if (!roleLocked) payload.role = f.role;
      if (f.role === "counselor" || staff.role === "counselor") {
        payload.campus = f.campus.trim();
        payload.max_capacity = Number(f.max_capacity) || 0;
      }
      if (f.new_password.trim()) payload.new_password = f.new_password;
      const { data } = await api.put(`/admin/staff/${staff.id}`, payload);
      toast.success(`${data.name} updated`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-staff-modal" className="max-w-md">
        <DialogHeader><DialogTitle>Edit {staff.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Full name">
            <Input data-testid="edit-staff-name" value={f.name} onChange={(e) => set("name")(e.target.value)} />
          </Field>
          <Field label="Login email">
            <Input data-testid="edit-staff-email" type="email" value={f.email} onChange={(e) => set("email")(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Role" hint={roleLocked ? "Super Admin role can't be changed here." : undefined}>
              <Select value={f.role} onValueChange={set("role")} disabled={roleLocked}>
                <SelectTrigger data-testid="edit-staff-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="counselor">Counselor</SelectItem>
                  <SelectItem value="dean">Dean</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {(f.role === "counselor" || staff.role === "counselor") && (
              <Field label="Lead capacity">
                <Input data-testid="edit-staff-capacity" type="number" min={0} value={f.max_capacity}
                  onChange={(e) => set("max_capacity")(e.target.value)} />
              </Field>
            )}
          </div>
          {(f.role === "counselor" || staff.role === "counselor") && (
            <Field label="Campus">
              <Input data-testid="edit-staff-campus" value={f.campus} onChange={(e) => set("campus")(e.target.value)} placeholder="Main Campus" />
            </Field>
          )}
          <Field label="Reset password (optional)" hint="Leave blank to keep the current password. Min 12 characters.">
            <Input data-testid="edit-staff-password" type="text" value={f.new_password}
              onChange={(e) => set("new_password")(e.target.value)} placeholder="New temporary password" />
          </Field>
          {seatBlocked && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="staff-seat-blocked">
              Counselor seat limit reached ({seats.used}/{seats.limit} on the {seats.tier} plan). Deactivate an
              unused counselor or ask your platform administrator to raise the limit.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="bg-brand" data-testid="edit-staff-submit"
            disabled={busy || f.name.trim().length < 2 || (f.new_password.trim() !== "" && f.new_password.length < 12) || seatBlocked}
            onClick={submit}>
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
